import { useState, useEffect, useCallback } from 'react';
import LayerHeader from '../components/ui/LayerHeader';
import { TH, makeSticky } from '../components/ui/gridTable';
import { fmt, fmtKc, MESICE_SHORT } from '../lib/formatters';
import { ACCENT, OK, BAD, VIOLET, W_MESIC, tableSurfaces } from '../lib/uiTokens';
import { AopTable, LayerSpinner } from '../components/ui/aopUi';
import useSirokyDisplej from '../hooks/useSirokyDisplej';

const sum = (a) => (a || []).reduce((x, y) => x + Number(y || 0), 0);
const sub = (a, b) => a.map((v, i) => Number(v || 0) - Number(b[i] || 0));
const addv = (a, b) => a.map((v, i) => Number(v || 0) + Number(b[i] || 0));

const W = { kat: 250, rocni: 150, mesic: W_MESIC };
const LEFT = { kat: 0, rocni: W.kat };

export default function FinanceAopSouhrn({ rok, variantaId, cardStyle, isDark }) {
    const siroky = useSirokyDisplej();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const qVar = variantaId ? `&varianta_id=${variantaId}` : '';
            setData(await fetch(`/api/finance/aop/souhrn?rok=${rok}${qVar}`).then(r => r.json()));
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rok, variantaId]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading || !data) {
        return <LayerSpinner />;
    }

    const { stickyBg, headBg, strongBg } = tableSurfaces(isDark);
    const th = TH;
    const sticky = makeSticky(LEFT, 'rocni', siroky ? null : ['kat']);

    // odvozené řady
    const prijmy = data.prijmy.celkem;
    const varN = data.variabilni.celkem;
    const fixN = data.ostatni.celkem;
    const marze = sub(prijmy, varN);
    const ebitda = sub(marze, fixN);
    const dphVystup = data.prijmy.dph_vystup;
    const dphVstup = addv(data.variabilni.dph_vstup, data.ostatni.dph_vstup);
    // DPH je průběžná (pass-through) položka — ani nesnižuje, ani nezvyšuje EBITDA.
    // DPH na výstupu vybíráme od zákazníků, z ní platíme dodavatelům DPH na vstupu
    // + zbytek odvádíme státu → vyruší se. „DPH k odvodu" je jen cash-flow / závazek.
    const dphOdvod = sub(dphVystup, dphVstup);

    const line = (key, label, vals, opts = {}) => {
        const { indent = 14, bold, color, strong } = opts;
        const rocni = sum(vals);
        const c = color || 'var(--text-primary)';
        const bg = strong ? strongBg : undefined;
        const sb = strong ? strongBg : stickyBg;
        return (
            <tr key={key} style={{ background: bg }}>
                <td className="py-1.5 pr-2" style={{ ...sticky('kat', 10, sb), paddingLeft: indent, color: c, fontWeight: bold ? 700 : 500, fontSize: 14 }}>{label}</td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ ...sticky('rocni', 10, sb), color: c, fontWeight: bold ? 700 : 500, fontSize: 14 }}>{fmtKc(rocni)}</td>
                {vals.map((v, i) => <td key={i} className="px-2 py-1.5 text-right font-mono tabular-nums text-[12px]" style={{ color: c, background: bg }}>{fmtKc(v)}</td>)}
            </tr>
        );
    };
    const band = (key, title, color) => (
        <tr key={key} style={{ background: headBg }}>
            <td className="px-2 py-2 text-[13px] font-bold uppercase tracking-wide" style={{ ...sticky('kat', 10, headBg), color }}>{title}</td>
            <td style={sticky('rocni', 10, headBg)}></td>
            {Array.from({ length: 12 }).map((_, i) => <td key={i} style={{ background: headBg }}></td>)}
        </tr>
    );

    const ebitdaColor = sum(ebitda) >= 0 ? OK : BAD;
    const marzeColor = sum(marze) >= 0 ? OK : BAD;

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Hlavička vrstvy */}
            <LayerHeader krok={6} title={`Souhrnný plán ${rok}`} cardStyle={cardStyle}>
                Konsolidace všech vrstev po kategoriích (bez DPH): Příjmy − Variabilní − Ostatní → marže a EBITDA.
                Dole rozpad DPH (na výstupu / na vstupu / k odvodu). Read-only — mění se editací jednotlivých vrstev.
            </LayerHeader>

            <AopTable cardStyle={cardStyle}>
                        <colgroup>
                            <col style={{ width: W.kat }} />
                            <col style={{ width: W.rocni }} />
                            {MESICE_SHORT.map((m, i) => <col key={i} style={{ width: W.mesic }} />)}
                        </colgroup>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                <th className={`${th} text-left`} style={{ ...sticky('kat', 30, headBg), color: 'var(--text-secondary)' }}>Kategorie</th>
                                <th className={`${th} text-right`} style={{ ...sticky('rocni', 30, headBg), color: ACCENT }}>Roční</th>
                                {MESICE_SHORT.map((m, i) => <th key={i} className={`${th} text-right`} style={{ color: 'var(--text-tertiary)', background: headBg }}>{m}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {band('h-p', 'Příjmy', ACCENT)}
                            {data.prijmy.kanaly.map((k, i) => line('p' + i, k.nazev, k.m, { indent: 26 }))}
                            {line('p-tot', 'Příjmy celkem (bez DPH)', prijmy, { bold: true })}

                            {band('h-v', 'Variabilní náklady', BAD)}
                            {data.variabilni.skupiny.map((s, i) => line('v' + i, s.label, s.m, { indent: 26 }))}
                            {line('v-tot', 'Variabilní náklady celkem', varN, { bold: true })}
                            {line('marze', 'Příspěvková marže', marze, { bold: true, strong: true, color: marzeColor })}

                            {band('h-f', 'Ostatní náklady', BAD)}
                            {data.ostatni.polozky.map((f, i) => line('f' + i, f.nazev, f.m, { indent: 26 }))}
                            {line('f-tot', 'Ostatní náklady celkem', fixN, { bold: true })}
                            {line('ebitda', 'EBITDA', ebitda, { bold: true, strong: true, color: ebitdaColor })}

                            {band('h-d', 'DPH — průběžná položka (mimo EBITDA)', VIOLET)}
                            {line('dv', 'DPH na výstupu (z příjmů)', dphVystup, { indent: 26, color: OK })}
                            {line('dvs', 'DPH na vstupu (z nákladů)', dphVstup, { indent: 26, color: VIOLET })}
                            {line('do', 'DPH k odvodu státu (cash-flow, ne náklad)', dphOdvod, { bold: true, strong: true })}
                        </tbody>
                    </AopTable>

            <p className="text-[13px] mt-3 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                <b>DPH je průběžná položka a nevstupuje do EBITDA</b> (ani nesnižuje, ani nezvyšuje). Všechny částky jsou
                bez DPH, takže EBITDA je reálný provozní výsledek. DPH na výstupu vybíráš od zákazníků nad rámec ceny bez
                DPH; z ní zaplatíš dodavatelům DPH na vstupu a zbytek („DPH k odvodu" = výstup − vstup) odvedeš státu —
                celé DPH se tak vyruší, nic ti z něj nezůstane.
                <br />
                DPH k odvodu je jen cash-flow / závazek — kolik hotovosti odložit pro finanční úřad (splatné do 25. dne
                následujícího měsíce; záporné = nadměrný odpočet). Není to náklad ani snížení/zvýšení zisku.
            </p>
        </div>
    );
}
